import { Component, attr } from '../Component'
import { element } from '../CustomElement'
import { i18n, friendlyValidity } from '../../i18n/I18n'

import { Link } from '../../components/flipp-link/Link'
import {
    LazyImage,
    SCREEN_RECT_OFFSET,
} from '../../components/flipp-lazy-image/LazyImage'

import { inject } from '../../services/di'
import { SeoService } from '../../services/SeoService'
import { Flyer } from '../../definitions/FlyerDefinitions'
import { AnalyticsService } from '../../services/AnalyticsService'
import {
    LandingPageActions,
    FavouriteContext,
    AggregateSourceAction,
    SourceAction,
    getCategorySourceActionValue,
} from '../../services/definitions/IAnalyticsService'
import { FlyerService } from '../../services/FlyerService'
import { Favourites, FavouriteService } from '../../services/FavouriteService'

import { LocationService } from '../../services/LocationService'
import { Rect } from '../../utils/Rect'
import { Timer } from '../../utils/Timer'
import { AuthenticationService } from '../../services/AuthenticationService'
import { AccountsUser } from '../../definitions/CommonDefinitions'
import { DateTimeService } from '../../services/DateTimeService'
import { AppsFlyerService } from '../../services/AppsFlyerService'
import { ConfigService } from '../../services/ConfigService'

export enum FlyerListingItemContext {
    Listing,
    LandingPage,
    Favourites,
    Search,
    CityStore,
}

export interface FlyerListingItemProps extends JSX.HTMLProps {
    flyer?: string | null
    flyerId: number
    context: FlyerListingItemContext
    premium?: boolean
    favourite?: boolean
    category?: string
    sde?: boolean // used as an element attribute in css
}

@element('flipp-flyer-listing-item')
export class FlyerListingItem extends Component<FlyerListingItemProps, Flyer> {
    @inject flyerService: FlyerService
    @inject favourites: FavouriteService
    @inject analytics: AnalyticsService
    @inject seo: SeoService
    @inject location: LocationService
    @inject auth: AuthenticationService
    @inject config: ConfigService

    @attr flyerId: number
    @attr flyer?: string
    @attr context: FlyerListingItemContext
    @attr premium: boolean
    @attr sde: boolean
    @attr category: string = 'All Flyers'

    refs: {
        spinner: HTMLElement
        flyerContainer: HTMLElement
    }

    private isFavourite: boolean = false
    private newFavourite: boolean = false
    private hasFavouriteChanged: boolean = false
    private isDisplayed: boolean = false
    private scrollDebouncer = new Timer(10, this.loadImageIfInViewport, this)

    async prepare() {
        this.loadImageIfInViewport()
        if (this.flyer) {
            return this.getPrepareFlyer()
        }
        const flyer = await this.flyerService.getFlyer(
            this.flyerId,
            false,
            true
        )
        const token = this.auth.user ? this.auth.user.token : null
        this.isFavourite = await this.favourites.isFavourite(flyer, token)
        return flyer
    }

    onAttach() {
        this.loadImageIfInViewport()
        window.addEventListener('scroll', this.handleViewportChange, true)
        window.addEventListener('resize', this.handleViewportChange, true)
        window.addEventListener(
            'flipp-favourites',
            this._handleFavouritesChanged
        )
    }

    onDetach() {
        window.removeEventListener(
            'flipp-favourites',
            this._handleFavouritesChanged
        )
    }

    onRender() {
        this.newFavourite = false
    }

    render(flyer: Flyer) {
        const attrs: { [key: string]: string } = {}
        if (this.newFavourite) {
            attrs['pulse'] = `${true}`
        }

        let sourceAction: AggregateSourceAction = SourceAction.BrowseFlyerClick

        switch (this.context) {
            case FlyerListingItemContext.Listing: {
                if (this.category === 'All Flyers') {
                    sourceAction = SourceAction.BrowseFlyerClick
                } else {
                    const name = this.category
                        ? this.category
                              .toLowerCase()
                              .trim()
                              .replace(/[ ]+/g, '_')
                        : 'all_flyers'

                    sourceAction = getCategorySourceActionValue(name)
                }
                break
            }
            case FlyerListingItemContext.Favourites:
                sourceAction = SourceAction.FavoriteClick
                break

            case FlyerListingItemContext.Search:
                sourceAction = SourceAction.SearchFlyerClick
                break

            case FlyerListingItemContext.CityStore:
                sourceAction = SourceAction.StoreFlyerClick
                break

            default:
                sourceAction = SourceAction.BrowseFlyerClick
                break
        }

        if (!this.isDisplayed) {
            return null
        }

        if (this.premium) {
            const thumbnail = flyer.thumbnailPremium
                ? flyer.thumbnailPremium
                : flyer.thumbnail
            return (
                <div>
                    <section ref="spinner" class="spinner-wrapper show">
                        <div class="loading">
                            <div class="spinner"></div>
                        </div>
                    </section>

                    <Link
                        ref="flyerContainer"
                        class="premium-flyer-container"
                        classList={{
                            favourite: this.isFavourite,
                            hidden: !this.hasFavouriteChanged,
                        }}
                        to={flyer}
                        onClick={this._handleLandingPageFlyerOpen}
                        sourceAction={sourceAction}
                    >
                        {!this.sde && (
                            <FlyerFavouriteButton
                                flyerId={this.flyerId}
                                context={this.context}
                                {...attrs}
                            />
                        )}

                        <LazyImage
                            class="flyer-thumbnail"
                            alt={this.getFlyersAltText(flyer)}
                            fit
                            cover
                            dataSrc={thumbnail}
                            onload={this.hideImageSpinner}
                        />
                        <div class="flyer-info">
                            <LazyImage
                                class="logo"
                                alt=""
                                fit
                                contain
                                dataSrc={flyer.merchantLogo}
                            />
                            <div class="flyer-info-block">
                                <p class="flyer-name">
                                    {this.getFlyerName(flyer)}
                                </p>
                                <p>
                                    {friendlyValidity(
                                        flyer.validFrom,
                                        flyer.validTo
                                    )}
                                </p>
                            </div>
                        </div>
                    </Link>
                </div>
            )
        } else {
            return (
                <div>
                    <section
                        ref="spinner"
                        class="spinner-wrapper non-premium show"
                    >
                        <div class="loading">
                            <div class="spinner"></div>
                        </div>
                    </section>
                    <Link
                        ref="flyerContainer"
                        class="flyer-container"
                        classList={{
                            favourite: this.isFavourite,
                            hidden: !this.hasFavouriteChanged,
                        }}
                        to={flyer}
                        sourceAction={sourceAction}
                    >
                        <FlyerFavouriteButton
                            flyerId={this.flyerId}
                            context={this.context}
                            {...attrs}
                        />
                        <LazyImage
                            class="flyer-thumbnail"
                            alt={this.getFlyersAltText(flyer)}
                            fit
                            cover
                            dataSrc={flyer.thumbnail}
                            onload={this.hideImageSpinner}
                        />
                        <p class="flyer-name">{this.getFlyerName(flyer)}</p>
                        <p>
                            {friendlyValidity(flyer.validFrom, flyer.validTo)}
                        </p>
                    </Link>
                </div>
            )
        }
    }

    private handleViewportChange = () => {
        this.scrollDebouncer.reset()
    }

    private isInViewport() {
        const screenRect = new Rect(
            0,
            0,
            window.innerWidth,
            window.innerHeight + SCREEN_RECT_OFFSET
        )
        const boundingRect = this.getBoundingClientRect()

        return Rect.intersects(screenRect, boundingRect as any)
    }

    private loadImageIfInViewport() {
        if (this.attached && this.isInViewport()) {
            this.isDisplayed = true

            // For some reason FlyerListingItem component on CityStorePage
            // at this moment has invalidation in progress
            // so we need to wait for invalidation to finish before running
            // other invalidation process
            this.context === FlyerListingItemContext.CityStore
                ? setTimeout(() => {
                      this.invalidate()
                  })
                : this.invalidate()
            this.unbindEvents()
        }
    }

    private unbindEvents() {
        window.removeEventListener('scroll', this.handleViewportChange, true)
        window.removeEventListener('resize', this.handleViewportChange, true)
    }

    private _handleFavouritesChanged = (e: CustomEvent) => {
        const newVal =
            (e.detail.data as Favourites).indexOf(this.model?.merchantId) !== -1
        const changed = newVal !== this.isFavourite
        if (newVal && !this.isFavourite) {
            // Item was just favourited. Track it so we can play the heart pulse!
            this.newFavourite = true
        }
        this.isFavourite = newVal
        if (changed) {
            this.hasFavouriteChanged = true
            this.invalidate()
        }
    }

    // FIXME_ivan: need to revisit this for merchant landing page and Items landing page.
    private _handleLandingPageFlyerOpen = () => {
        if (this.context === FlyerListingItemContext.LandingPage) {
            this.analytics.trackLandingPage(LandingPageActions.FlyerOpen)
        }
    }

    private getFlyerName = (flyer: Flyer) => {
        return `${flyer.merchant}`
    }
    private getFlyersAltText = (flyer: Flyer) => {
        const city = this.location.city
        const flyerName = this.getFlyerName(flyer)

        if (this.category !== 'All Flyers') {
            return i18n('category_flyers_alt', {
                city,
                flyerName,
                category: i18n(this.category),
            })
        }

        return i18n('index_flyers_alt', {
            city,
            flyerName,
        })
    }

    private hideImageSpinner = () => {
        this.refs.spinner.classList.remove('show')
        this.refs.flyerContainer.classList.remove('hidden')
    }

    private getPrepareFlyer = (): Flyer => {
        const flyer: any = JSON.parse(this.flyer)
        const dtService = DateTimeService.getInstance()
        return new Flyer({
            ...flyer,
            thumbnail_url: flyer.premium_thumbnail_url,
            available_from: dtService
                .convertTimeToZulu(flyer.available_from)
                .toISOString(),
            available_to: dtService
                .convertTimeToZulu(flyer.available_to, true)
                .toISOString(),
            valid_from: dtService
                .convertTimeToZulu(flyer.valid_from)
                .toISOString(),
            valid_to: dtService
                .convertTimeToZulu(flyer.valid_to, true)
                .toISOString(),
        })
    }
}

// --- flipp-flyer-favourite-button ------------------------------>

interface FlyerFavouriteButtonProps {
    flyerId: number
    context: FlyerListingItemContext
}

@element('flipp-flyer-favourite-button')
class FlyerFavouriteButton extends Component<FlyerFavouriteButtonProps, Flyer> {
    @inject flyerService: FlyerService
    @inject favourites: FavouriteService
    @inject analytics: AnalyticsService
    @inject auth: AuthenticationService
    @inject appsFlyerService: AppsFlyerService

    @attr flyerId: number
    @attr context: FlyerListingItemContext

    private isFavourite: boolean = false
    private token: AccountsUser

    async prepare() {
        const flyer = await this.flyerService.getFlyer(this.flyerId, false)
        this.token = this.auth.user ? this.auth.user.token : null
        this.isFavourite = await this.favourites.isFavourite(flyer, this.token)
        return flyer
    }

    render() {
        return (
            <button
                aria-label={i18n('fav_page')}
                ref="heart"
                onClick={this._toggleFavourite}
            >
                <div flex-horizontal>
                    {this.isFavourite ? (
                        <img
                            class="full-heart"
                            src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-fill.svg"
                            alt=""
                        />
                    ) : (
                        <img
                            class="empty-heart"
                            src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-outline.svg"
                            alt=""
                        />
                    )}
                </div>
            </button>
        )
    }

    private _toggleFavourite = (evt: Event) => {
        evt.preventDefault()
        evt.stopPropagation()
        if (this.isFavourite) {
            this.favourites.removeFavourite(this.model, this.token)
        } else {
            this.favourites.addFavourite(this.model, this.token)
        }

        this.analytics.trackFavouriteEvent(
            this.model.merchantId,
            this.isFavourite ? 'Favorited' : 'Unfavorited',
            this._getFavouriteContext(this.context)
        )
        if (!this.isFavourite) this.appsFlyerService.trackAddFavouriteEvent()
    }

    private _getFavouriteContext = (context: FlyerListingItemContext) => {
        switch (context) {
            case 0:
                return FavouriteContext.Listing
            case 2:
                return FavouriteContext.Favourites
            case 3:
                return FavouriteContext.Search
            case 1:
            default:
                return null /* N/A (Landing page) */
        }
    }
}

// --- flipp-tombstone-listing-item --------------------------------------->

export interface TombstoneListingItemProps extends JSX.HTMLAttributes {
    merchantLogo: string
}

@element('flipp-tombstone-listing-item')
export class TombstoneListingItem extends Component<
    TombstoneListingItemProps,
    any
> {
    @attr merchantLogo: string

    render() {
        return (
            <div class="flyer-container">
                <div class="tombstone-img-container">
                    <img
                        class="tombstone-thumbnail"
                        src={this.merchantLogo}
                        alt=""
                        contain
                    />
                </div>
                <div class="tombstone-msg">{i18n('tombstone_msg')}</div>
            </div>
        )
    }
}
